import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../Helpers/Handler";
import { textCapitalize } from "../../Helpers/Utils";

// 2026-09-16: modelId is now an optional prop. Opened from a model page it is
// supplied and the pickers are hidden; opened from the standalone Variants
// page the admin chooses brand -> model here. Brand is only a narrowing device
// for the model list - the variant is created under the model.
const VariantFormModal = ({ mode = "create", modelId, variant, onClose, onSaved }) => {
    const modalRef = useRef(null);
    const bsModalRef = useRef(null);
    const needsModelPicker = mode === "create" && !modelId;
    const [brands, setBrands] = useState([]);
    const [models, setModels] = useState([]);
    const [loadingBrands, setLoadingBrands] = useState(needsModelPicker);
    const [loadingModels, setLoadingModels] = useState(false);
    const [selectedBrandId, setSelectedBrandId] = useState("");
    const [selectedModelId, setSelectedModelId] = useState(modelId || "");
    const [variantName, setVariantName] = useState(variant?.variantName || "");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const el = modalRef.current;
        if (!el || !window.bootstrap) return undefined;
        const instance = window.bootstrap.Modal.getOrCreateInstance(el);
        bsModalRef.current = instance;
        const handleHidden = () => onClose?.();
        el.addEventListener("hidden.bs.modal", handleHidden);
        instance.show();
        return () => {
            el.removeEventListener("hidden.bs.modal", handleHidden);
            instance.hide();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!needsModelPicker) return;
        (async () => {
            setLoadingBrands(true);
            const response = await Handler({ method: "GET", url: "/admin/vehicle/brand/active" });
            setBrands(response?.success ? response?.data?.data || [] : []);
            setLoadingBrands(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // No brand picked yet means every active model, so a variant can still be
    // created without narrowing first.
    useEffect(() => {
        if (!needsModelPicker) return;
        if (!selectedBrandId) return
        (async () => {
            setLoadingModels(true);
            const response = await Handler({
                method: "GET",
                url: "/admin/vehicle/model/active",
                params: selectedBrandId ? { brandId: selectedBrandId } : {},
            });
            setModels(response?.success ? response?.data?.data || [] : []);
            setLoadingModels(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBrandId]);

    const onBrandChange = (value) => {
        setSelectedBrandId(value);
        setSelectedModelId("");
    };

    const onSubmit = async () => {
        const name = variantName.trim();
        if (needsModelPicker && !selectedModelId) {
            toast.error("Select a model");
            return;
        }
        if (!name) {
            toast.error("Variant name is required");
            return;
        }
        setSaving(true);
        const payload = { variantName: name };
        const response = mode === "edit"
            ? await Handler({ method: "patch", url: `/admin/vehicle/variant/${variant.variantId}`, data: payload })
            : await Handler({ method: "post", url: `/admin/vehicle/model/${selectedModelId}/variant`, data: payload });

        if (response?.success) {
            toast.success(response?.data?.message || (mode === "edit" ? "Variant updated" : "Variant created"));
            onSaved?.();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to save the variant"));
        }
        setSaving(false);
    };

    return (
        <div className="modal fade" tabIndex="-1" ref={modalRef} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 rounded-4">
                    <div className="modal-header">
                        <h5 className="modal-title fw-semibold">{mode === "edit" ? "Edit Variant" : "Add Variant"}</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="row g-3">
                            {needsModelPicker ? (
                                <>
                                    <div className="col-12 col-md-6">
                                        <label className="form-label">Brand <span className="text-muted small">(optional filter)</span></label>
                                        <select className="form-select" value={selectedBrandId} onChange={(e) => onBrandChange(e.target.value)} disabled={loadingBrands || saving}>
                                            <option value="">{loadingBrands ? "Loading..." : "All brands"}</option>
                                            {brands.map((b) => (
                                                <option key={b.brandId} value={b.brandId}>{b.brand}{b.brandType ? ` (${b.brandType})` : ""}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <label className="form-label">Model</label>
                                        <select className="form-select" value={selectedModelId} onChange={(e) => setSelectedModelId(e.target.value)} disabled={loadingModels || saving}>
                                            <option value="" disabled>{loadingModels ? "Loading..." : "Select model"}</option>
                                            {models.map((m) => (
                                                <option key={m.modelId} value={m.modelId}>{m.brand ? `${m.brand} ` : ""}{m.modelName}</option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            ) : ""}
                            <div className="col-12">
                                <label className="form-label">Variant name</label>
                                <input type="text" className="form-control" value={variantName} disabled={saving} onChange={(e) => setVariantName(e.target.value)} placeholder="e.g. VXI, ZXI+" />
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal" disabled={saving}>Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={saving}>
                            {saving ? "Saving..." : mode === "edit" ? "Save changes" : "Create variant"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VariantFormModal;
